import { useEffect, useState, useRef, memo } from 'react';
import MarkdownRenderer from '../Common/MarkdownRenderer';

const CHARS_PER_TICK = 56;

const buildChunks = (safeText) => {
  const chunks = [];
  const lines = safeText.split('\n');
  lines.forEach((line, idx) => {
    for (let i = 0; i < line.length; i += CHARS_PER_TICK) {
      chunks.push(line.slice(i, i + CHARS_PER_TICK));
    }
    if (idx < lines.length - 1) {
      chunks.push('\n');
    }
  });
  return chunks;
};

const MarkdownAutoTyping = memo(({ text, speed = 10, setTypingMethod }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const hasTypedRef = useRef(false);
    const textRef = useRef(text);

    useEffect(() => {
        // If we've already typed this exact text, don't type again
        if (hasTypedRef.current && textRef.current === text) return;

        // If text changed, reset and type the new text
        if (textRef.current !== text) {
            hasTypedRef.current = false;
            textRef.current = text;
        }

        const safeText = text || '';

        if (!safeText) {
            setIsTyping(false);
            if (setTypingMethod) setTypingMethod(false);
            return;
        }

        setDisplayedText('');
        setIsTyping(true);

        // Chunk by newlines + fixed-length slices only — do NOT split on ".!? ",
        // which can drop punctuation or confuse incremental react-markdown parses.
        const chunks = buildChunks(safeText);

        const indexRef = { current: 0 };

        const interval = setInterval(() => {
            if (setTypingMethod) setTypingMethod(true);

            if (indexRef.current < chunks.length) {
                setDisplayedText((prev) => prev + chunks[indexRef.current]);
                indexRef.current++;
            } else {
                clearInterval(interval);
                setIsTyping(false);
                hasTypedRef.current = true;
                if (setTypingMethod) setTypingMethod(false);
            }
        }, speed * 5);

        return () => {
            clearInterval(interval);
        };
    }, [text, speed, setTypingMethod]);

    return (
        <div style={{ position: 'relative', display: 'block', width: '100%', maxWidth: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            <MarkdownRenderer 
              content={displayedText || ''} 
              showCursor={isTyping} 
            />
        </div>
    );
});

MarkdownAutoTyping.displayName = 'MarkdownAutoTyping';

export default MarkdownAutoTyping;
